import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { SettingsService } from './settings.service';

const TRANSLATION_CACHE_PREFIX = 'translation:';

@Injectable({
  providedIn: 'root'
})
export class CustomTranslateLoader implements TranslateLoader {

  constructor(
    private httpClient: HttpClient,
    private api: ApiService,
    private settings: SettingsService) {
  }

  getTranslation(language: string): Observable<any> {

    return from(this.loadTranslation(language))
      .pipe(
        map(translations => this.normalizeLegacyPlaceholders(translations) as object),
        tap(x => {
          if (!Object.keys(x).length)
            throw Error('No translations found');
        }),
        catchError(_ => this.httpClient.get('assets/i18n/en.json'))

      );
  }

  private async loadTranslation(language: string): Promise<object> {
    try {
      const translations = await this.api.getTranslationData(language);
      if (!translations || !Object.keys(translations).length)
        throw Error('No translations found');

      // Keep the last successful response for offline startup. The server
      // remains authoritative whenever it can be reached.
      try {
        await this.settings.setByKey(this.cacheKey(language), translations);
      } catch {
        // Translation loading should not fail just because local storage is
        // unavailable.
      }

      return translations;
    } catch {
      const cachedTranslations = await this.getCachedTranslation(language);
      if (cachedTranslations)
        return cachedTranslations;

      return firstValueFrom(this.httpClient.get<object>('assets/i18n/en.json'));
    }
  }

  private async getCachedTranslation(language: string): Promise<object | null> {
    try {
      const translations = await this.settings.getByKey<object>(this.cacheKey(language));
      return translations && Object.keys(translations).length ? translations : null;
    } catch {
      return null;
    }
  }

  private cacheKey(language: string) {
    return `${TRANSLATION_CACHE_PREFIX}${language}`;
  }

  private normalizeLegacyPlaceholders(value: unknown): unknown {
    if (typeof value === 'string') {
      return value.replace(/\{(?!\{)\s*([A-Za-z_][A-Za-z0-9_.]*)\s*\}(?!\})/g, '{{$1}}');
    }

    if (Array.isArray(value)) {
      return value.map(item => this.normalizeLegacyPlaceholders(item));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce((result, [key, item]) => {
        result[key] = this.normalizeLegacyPlaceholders(item);
        return result;
      }, {} as Record<string, unknown>);
    }

    return value;
  }
}
